import type {
  AggregationDescriptor,
  ChartConfiguration,
  DimensionDescriptor,
  FinancialStatementConfiguration,
  ListConfiguration,
  MeasureDescriptor,
  MetricDescriptor,
  PivotConfiguration,
  ReportConfiguration,
  ReportType,
  TabularConfiguration,
  TileConfiguration,
} from "../../../../../../api/types/svTypes.ts";
import { addMissingGroupSorts, setGroupingFlag } from "../../components/builder/common/utilities/sortFieldStateHelper";
import { configurationToArguments } from "../../components/builder/utils/configuration/chartConfiguration";
import {
  configurationToConditions,
  parseReportConfiguration,
  configurationToSorts,
} from "../../components/builder/utils/configuration/configurations";
import {
  configurationToColumns,
  configurationToRows,
  configurationToValues,
} from "../../components/builder/utils/configuration/pivotConfigurations";
import {
  configurationToGrouping,
  configurationToTabularFields,
} from "../../components/builder/utils/configuration/tabularConfiguration";
import { configurationToFinancialStatementState } from "../../components/builder/utils/configuration/financialStatementConfiguration";
import { configurationToListFields } from "../../components/builder/utils/configuration/listConfiguration";
import { configurationToTileFields } from "../../components/builder/utils/configuration/tileConfiguration";
import {
  isChartReportType,
  isFinancialStatementReportType,
  isListReportType,
  isPivotReportType,
  usesTabularConfiguration,
  isTileReportType,
} from "../../utilities/reportTypeHelper.ts";
import type { AppDispatch } from "../store";
import { fieldsStateActions } from "../thunks/fieldStatesThunks";
import { createFinancialStatementAction } from "./storeUtils";

export const setFieldStatesConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  reportType: ReportType,
  dispatch: AppDispatch,
  aggregations: AggregationDescriptor[] = []
) => {
  const configuration = parseReportConfiguration(reportConfiguration, reportType);
  if (usesTabularConfiguration(reportType)) {
    setTabularConfiguration(dimensions, measures, metrics, configuration, dispatch, aggregations);
  } else if (isPivotReportType(reportType)) {
    setPivotReportConfiguration(dimensions, measures, metrics, configuration, dispatch, aggregations);
  } else if (isChartReportType(reportType)) {
    setChartReportConfiguration(dimensions, measures, metrics, configuration, dispatch, aggregations);
  } else if (isTileReportType(reportType)) {
    setTileReportConfiguration(dimensions, measures, metrics, configuration, dispatch, aggregations);
  } else if (isListReportType(reportType)) {
    setListReportConfiguration(dimensions, measures, configuration, dispatch);
  } else if (isFinancialStatementReportType(reportType)) {
    setFinancialStatementReportConfiguration(dimensions, measures, metrics, configuration, dispatch);
  } else {
    throw new Error("Report type not supported");
  }
};

const setFinancialStatementReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as FinancialStatementConfiguration;
  const state = configurationToFinancialStatementState(configuration, dimensions, measures, metrics);
  dispatch(fieldsStateActions.setConditions(state.conditions));
  dispatch(createFinancialStatementAction("setLines", state.lines));
  dispatch(createFinancialStatementAction("setAxes", state.axes));
  dispatch(createFinancialStatementAction("setDataColumns", state.dataColumns));
  dispatch(fieldsStateActions.setSettings({ settings: state.settings }));
};

const setListReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch
) => {
  const configuration = reportConfiguration as ListConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.fields) {
    const result = configurationToListFields(configuration.fields, dimensions);
    dispatch(fieldsStateActions.setFields(result));
  }
  if (configuration.sort) {
    const result = configurationToSorts(configuration.sort, dimensions, measures, configuration.fields ?? []);
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames: [] }));
  }
  dispatch(fieldsStateActions.setLedger(configuration.ledger));
  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};

const setTileReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch,
  aggregations: AggregationDescriptor[] = []
) => {
  const configuration = reportConfiguration as TileConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.fields) {
    const result = configurationToTileFields(configuration.fields, dimensions, measures, metrics, aggregations);
    dispatch(fieldsStateActions.setFields(result));
  }
  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};

const setChartReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch,
  aggregations: AggregationDescriptor[] = []
) => {
  const configuration = reportConfiguration as ChartConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.arguments) {
    const result = configurationToArguments(configuration.arguments, dimensions);
    dispatch(fieldsStateActions.setArguments(result));
  }
  if (configuration.values) {
    const result = configurationToValues(configuration.values, measures, metrics, dimensions, aggregations);
    dispatch(fieldsStateActions.setMeasures(result));
  }
  if (configuration.sort) {
    const reportFields = [...(configuration.arguments ?? []), ...(configuration.values ?? [])];
    const result = configurationToSorts(configuration.sort, dimensions, measures, reportFields);
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames: [] }));
  }
  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};

const setPivotReportConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch,
  aggregations: AggregationDescriptor[] = []
) => {
  const configuration = reportConfiguration as PivotConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }
  if (configuration.rows) {
    const result = configurationToRows(configuration.rows, dimensions, measures);
    dispatch(fieldsStateActions.setRows(result));
  }
  if (configuration.columns) {
    const result = configurationToColumns(configuration.columns, dimensions, measures);
    dispatch(fieldsStateActions.setColumns(result));
  }
  if (configuration.values) {
    const result = configurationToValues(configuration.values, measures, metrics, dimensions, aggregations);
    dispatch(fieldsStateActions.setMeasures(result));
  }
  if (configuration.sort) {
    const reportFields = [
      ...(configuration.rows ?? []),
      ...(configuration.columns ?? []),
      ...(configuration.values ?? []),
    ];
    const result = configurationToSorts(configuration.sort, dimensions, measures, reportFields);
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames: [] }));
  }
  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};

const setTabularConfiguration = (
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  reportConfiguration: ReportConfiguration,
  dispatch: AppDispatch,
  aggregations: AggregationDescriptor[] = []
) => {
  const configuration = reportConfiguration as TabularConfiguration;
  if (!configuration) {
    return;
  }
  if (configuration.conditions) {
    const result = configurationToConditions(configuration.conditions, dimensions);
    dispatch(fieldsStateActions.setConditions(result));
  }

  if (configuration.fields) {
    const result = configurationToTabularFields(configuration.fields, dimensions, measures, metrics, aggregations);
    dispatch(fieldsStateActions.setFields(result));
  }

  if (configuration.sort) {
    const sorts = addMissingGroupSorts(
      configuration.fields,
      configuration.grouping,
      setGroupingFlag(configuration.fields ?? [], configuration.grouping ?? [], configuration.sort ?? [])
    );
    const result = configurationToSorts(sorts, dimensions, measures, configuration.fields ?? []);
    const groupMetaNames = configuration.grouping.map(
      (g) => configuration.fields.find((f) => f.guid === g.id)?.name ?? ""
    );
    dispatch(fieldsStateActions.setSorting({ fields: result, groupMetaNames }));
  }

  if (configuration.grouping) {
    const result = configurationToGrouping(configuration.grouping);
    dispatch(fieldsStateActions.setGrouping(result));
  }

  if (configuration.settings) {
    dispatch(fieldsStateActions.setSettings({ settings: configuration.settings }));
  }
};
